import { inject } from '@angular/core';
import {
  ResolveFn,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { PanelService } from '../../shared/services/api/panel.service';
import { Url_Friendly_String } from '../../shared/helpers/helper';
import { FilterChipItem } from '../../shared/models/filter-chip.model';
import { StoreCategoryV2 } from '../../shared/models/category.model';
import { StoreCountry } from '../../shared/models/location.model';
import { PaginatedResponse } from '../../shared/models/pagination.model';
import { ProductV2 } from '../../shared/models/products-v2.model';

const DEFAULT_SORT = 'created_time';
const DEFAULT_PAGE_SIZE = 24;
const ALL_CATEGORIES_AVATAR =
  'https://d1upatzsvnpphr.cloudfront.net/broker/notification_system/media/file/all-categories.svg';

export interface CategoryResolvedData {
  categoryList: FilterChipItem[];
  productListResponse: PaginatedResponse<ProductV2>;
  currentCategory: StoreCategoryV2 | null;
  countryCode: string;
  categoryId: string | undefined;
  categoryName: string | undefined;
  sortFromQuery: string | null;
  searchFromQuery: string | null;
}

function buildCategoryList(
  categories: StoreCategoryV2[],
  countryCode: string,
  categoryId: string | undefined
): FilterChipItem[] {
  const allCategories: FilterChipItem[] = [
    {
      label: 'All',
      url: `/category/${countryCode}/all`,
      isActive: !categoryId,
      avatar: ALL_CATEGORIES_AVATAR
    }
  ];

  const blackfridayIndex = categories.findIndex(
    (c) => c.name.toLowerCase() === 'black friday'
  );
  if (blackfridayIndex > -1) {
    const blackfriday = categories.splice(blackfridayIndex, 1)[0];
    allCategories.push({
      label: blackfriday.name,
      url: `/category/${countryCode}/${blackfriday.category_id}/${Url_Friendly_String(blackfriday.name)}`,
      isActive: String(blackfriday.category_id) === String(categoryId),
      avatar: blackfriday.avatar,
      data: { cssClass: 'black-friday-category', isBlackFriday: true }
    });
  }
  return [
    ...allCategories,
    ...categories.map((c: StoreCategoryV2) => ({
      label: c.name,
      url: `/category/${countryCode}/${c.category_id}/${Url_Friendly_String(c.name)}`,
      isActive: String(c.category_id) === String(categoryId),
      avatar: c.avatar
    }))
  ];
}

export const categoryResolver: ResolveFn<
  CategoryResolvedData | UrlTree
> = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
): Observable<CategoryResolvedData | UrlTree> => {
  const router = inject(Router);
  const panelService = inject(PanelService);

  const countryCode =
    route.parent?.params['countryCode'] ?? route.params['countryCode'] ?? '';
  const categoryId = route.params['id'];
  const categoryName = route.params['name'];
  const sortFromQuery = route.queryParamMap.get('sort');
  const searchFromQuery = route.queryParamMap.get('search');

  if (!countryCode) {
    return of(router.parseUrl('/not-found'));
  }

  const ordering = sortFromQuery ?? DEFAULT_SORT;
  const productParams: Record<string, unknown> = {
    limit: DEFAULT_PAGE_SIZE,
    offset: 0,
    ordering,
    categories: categoryId || undefined,
    search: searchFromQuery || undefined,
    has_discount: false
  };

  return panelService.getRegionList({ ordering: 'name' }).pipe(
    take(1),
    catchError(() => of([] as StoreCountry[])),
    map((raw: StoreCountry[] | { results?: StoreCountry[] }) =>
      Array.isArray(raw) ? raw : (raw?.results ?? [])
    ),
    switchMap((regions: StoreCountry[]) => {
      const region = regions.find((r) => r.code === countryCode.toUpperCase());
      if (region?.id) {
        productParams['show_countries'] = region.id;
      }
      return panelService.getCategoryListV2({ is_collection: false }).pipe(
        take(1),
        catchError(() => of(router.parseUrl('/not-found'))),
        switchMap((categoriesOrTree): Observable<CategoryResolvedData | UrlTree> => {
          if (categoriesOrTree instanceof UrlTree) {
            return of(categoriesOrTree);
          }
          const categories = categoriesOrTree as StoreCategoryV2[];

          if (categoryId) {
            const currentCategory = categories.find(
              (c) => String(c.category_id) === String(categoryId)
            );
            if (!currentCategory) {
              return of(router.parseUrl('/not-found'));
            }
            const expectedName = Url_Friendly_String(currentCategory.name);
            if (categoryName && expectedName !== categoryName) {
              return of(
                router.createUrlTree(
                  ['/category', countryCode, categoryId, expectedName],
                  { queryParams: route.queryParams }
                )
              );
            }
          }

          return panelService.getProductListV2(productParams, true).pipe(
            take(1),
            map((productListResponse) => {
              if (
                !productListResponse ||
                typeof productListResponse.count === 'undefined'
              ) {
                return router.parseUrl('/not-found');
              }
              return {
                categoryList: buildCategoryList(
                  categories,
                  countryCode,
                  categoryId
                ),
                productListResponse,
                currentCategory: categoryId
                  ? (categories.find(
                      (c) => String(c.category_id) === String(categoryId)
                    ) ?? null)
                  : null,
                countryCode,
                categoryId,
                categoryName,
                sortFromQuery,
                searchFromQuery
              } as CategoryResolvedData;
            }),
            catchError(() => of(router.parseUrl('/not-found')))
          );
        })
      );
    }),
    catchError(() => of(router.parseUrl('/not-found'))),
    take(1)
  );
};
